import { useApiClient, useErrorHandler, useToastService } from "@@common";
import { IExpression, ObjectVariableScope } from "@@expressions";
import { Alert, Box, Button, Typography } from "@mui/material";
import React, { Suspense, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { OperationClient } from "../../../api/api";
import { CoreContentTemplatesClient } from "../../../api/CoreContentTemplatesClient";
import { ReactChatClient } from "../../../api/ReactChatClient";
import { ReactCoreClient } from "../../../api/ReactCoreClient";
import Spinner from "../../../common/components/Spinner";
import AuthContext from "../../../components/AuthContext";
import ConfigContext from "../../../components/ConfigContext";
import EmbedLoading from "../../../embedding/EmbedLoading";
import { EMPTY_GUID } from "../../../ExpressionsModule/Runtime/Evaluators/DomainServices/EMPTY_GUID";
import { VariableNames } from "../../Constants/VariableNames";
import BundleContext from "../../Contexts/BundleContext";
import { useDesignerLiveChat } from "../../Contexts/DesignerLiveChatContext";
import { ReactBundleContext } from "../../Contexts/ReactBundleContext";
import { createReactEnumContext } from "../../Contexts/ReactEnumContext";
import type { ReactComponentContext } from "../../Contexts/ReactRuntimeBridge";
import { useComponentSelection } from "../../Hooks/useComponentSelection";
import useComponentVisibility from "../../Hooks/useComponentVisibility";
import useObservedExpression from "../../Hooks/useObservedExpression";
import { ReactComponentHostModel } from "../../Models/ReactComponentHostModel";
import { ComponentService } from "../../Services/ComponentService";
import { IsDesignMode } from "../../Utils/runtimeDetection";
import IComponentProps from "../IComponentProps";
import getComponentLayout from "../Table/getComponentLayout";
import ReactComponentErrorBoundary from "./ReactComponentErrorBoundary";
import { resolveReactOperation } from "./resolveReactOperation";
import { useReactCurrentUser } from "./useReactCurrentUser";
import { useReactRoute } from "./useReactRoute";

const RenderedComponent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const configuration = useContext(ConfigContext);
  useEffect(() => {
    configuration?.EMBED_PERFORMANCE?.("generated-component-committed");
  }, [configuration]);
  return <>{children}</>;
};

const ReactComponentHost: React.FC<IComponentProps<ReactComponentHostModel>> = ({ component, context, componentPath = [] }) => {
  const reactBundle = useContext(ReactBundleContext);
  const auth = useContext(AuthContext);
  const config = useContext(ConfigContext);
  const bundle = useContext(BundleContext);
  const navigate = useNavigate();
  const route = useReactRoute();
  const currentUser = useReactCurrentUser();
  const operationClient = useApiClient(OperationClient);
  const coreClient = useApiClient(ReactCoreClient);
  const liveChat = useDesignerLiveChat();
  const chatClient = useApiClient(ReactChatClient, { signal: IsDesignMode ? liveChat.signal : undefined });
  const contentTemplatesClient = useApiClient(CoreContentTemplatesClient);
  const errorHandler = useErrorHandler();
  const toastService = useToastService();
  const [input, inputPending] = useObservedExpression(component.Input, context, "ReactComponentHost.Input");
  const resolvedInput = useRef<{ value: unknown }>();
  if (!inputPending) resolvedInput.current = { value: input };
  const visibility = useComponentVisibility(component, context, "ReactComponentHost");
  const selectionProps = useComponentSelection({ component, componentPath });
  const isSelectedInDesigner = selectionProps.className?.split(" ").includes("iw-selected") ?? false;
  const componentName = component.ComponentName?.trim();
  const componentReference = componentName ?? component.Id;
  const bindingStatus = reactBundle.componentStatus(componentName, component.Id);
  const isBound = bindingStatus === "bound";

  const GeneratedComponent = useMemo(
    () => (isBound ? React.lazy(() => reactBundle.loadComponent(componentName, component.Id)) : null),
    [component.Id, componentName, isBound, reactBundle]
  );

  const evaluateNamedExpression = useCallback(
    async (expressions: Record<string, IExpression> | undefined, name: string, variableName: string, value: unknown) => {
      const expression = expressions?.[name];
      if (!expression) {
        throw new Error(`React component '${component.ComponentName}' requested unknown capability '${name}'.`);
      }

      const variableScope = new ObjectVariableScope({ [variableName]: value }, context.VariableScope);
      const eventContext = context.CreateChild(variableScope);
      return eventContext.Evaluator.evaluate(expression, eventContext);
    },
    [component.ComponentName, context]
  );

  const invoke = useCallback(
    async <TRequest = unknown, TResponse = unknown>(operationReference: string, operationInput?: TRequest): Promise<TResponse> => {
      const allowedOperation = resolveReactOperation(component.Operations, operationReference, componentReference);

      const viewId = context.GetProperty<ComponentService>(ComponentService.KEY)?.getViewComponentId() ?? EMPTY_GUID;
      try {
        const response: unknown = await operationClient.execute(
          allowedOperation.Id,
          allowedOperation.Name ?? operationReference,
          viewId,
          bundle.Id ?? EMPTY_GUID,
          IsDesignMode,
          operationInput
        );
        return response as TResponse;
      } catch (error) {
        errorHandler(error);
        throw error;
      }
    },
    [bundle.Id, component.Operations, componentReference, context, errorHandler, operationClient]
  );
  const emit = useCallback(
    (name: string, eventValue?: unknown) => evaluateNamedExpression(component.Events, name, VariableNames.Event, eventValue),
    [component.Events, evaluateNamedExpression]
  );
  const navigateTo = useCallback((path: string) => navigate(path), [navigate]);
  const enums = useMemo(() => createReactEnumContext(Object.values(bundle.EnumById)), [bundle.EnumById]);
  const core = useMemo(
    () => ({
      ...coreClient.forHost(bundle.Id ?? EMPTY_GUID, component.Id),
      chat: chatClient.forHost(bundle.Id ?? EMPTY_GUID, component.Id, IsDesignMode && !liveChat.enabled, IsDesignMode ? liveChat.canUse : undefined),
      contentTemplates: {
        search: (request?: Parameters<CoreContentTemplatesClient["search"]>[0]) => contentTemplatesClient.search(request),
        getById: (templateId: string) => contentTemplatesClient.getById(templateId),
      },
    }),
    [bundle.Id, component.Id, contentTemplatesClient, coreClient, chatClient, liveChat.enabled, liveChat.canUse]
  );
  const componentContext = useMemo<ReactComponentContext>(
    () => ({ invoke, emit, navigate: navigateTo, enums, route, currentUser, toastService, core }),
    [emit, invoke, navigateTo, enums, route, currentUser, toastService, core]
  );

  if (!visibility.isVisible) {
    return null;
  }

  const resetKey = `${reactBundle.buildId ?? "none"}:${componentReference}:${IsDesignMode ? liveChat.enabled : "runtime"}`;
  return (
    <Box
      {...selectionProps}
      className={[selectionProps.className, "iw-react-component-host"].filter(Boolean).join(" ")}
      sx={[getComponentLayout(component), visibility.hiddenSx, ...(Array.isArray(selectionProps.sx) ? selectionProps.sx : [selectionProps.sx ?? {}])]}
    >
      {isSelectedInDesigner && (
        <Box
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            zIndex: 1300,
            maxWidth: "calc(100% - 8px)",
            px: 0.875,
            py: 0.375,
            borderRadius: 1,
            bgcolor: "primary.main",
            boxShadow: 1,
            color: "primary.contrastText",
            fontSize: "0.7rem",
            fontWeight: 500,
            lineHeight: 1.25,
            overflow: "hidden",
            pointerEvents: "none",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          React · {componentName ?? component.Id}
        </Box>
      )}
      {!IsDesignMode && !config?.EMBED_FETCH && component.Chat && !auth.isAuthenticated ? (
        <Alert
          severity="info"
          action={
            <Button color="inherit" onClick={() => void auth.authService.signIn(window.location.pathname + window.location.search)}>
              Sign in
            </Button>
          }
        >
          Sign in to use this Chat in standalone mode. Anonymous Chat requires an App Embed session.
        </Alert>
      ) : bindingStatus === "loading" ? (
        config?.EMBED_FETCH ? (
          <EmbedLoading />
        ) : (
          <Spinner />
        )
      ) : GeneratedComponent ? (
        <ReactComponentErrorBoundary resetKey={resetKey} componentName={componentReference}>
          <Suspense fallback={config?.EMBED_FETCH ? <EmbedLoading /> : <Spinner />}>
            {resolvedInput.current && (
              <RenderedComponent>
                <GeneratedComponent key={resetKey} input={resolvedInput.current.value} ctx={componentContext} />
              </RenderedComponent>
            )}
            {!resolvedInput.current && config?.EMBED_FETCH && <EmbedLoading />}
          </Suspense>
        </ReactComponentErrorBoundary>
      ) : (
        IsDesignMode && (
          <Box
            aria-label="Unconfigured React component host"
            sx={{
              alignItems: "center",
              backgroundColor: "action.hover",
              border: "1px dashed",
              borderColor: "primary.main",
              borderRadius: 1,
              display: "flex",
              justifyContent: "center",
              minHeight: 96,
              p: 2,
              textAlign: "center",
            }}
          >
            <Box>
              <Typography color="primary" variant="subtitle2">
                React component host
              </Typography>
              <Typography color="text.secondary" variant="body2">
                Select this host and ask the designer to create its React UI.
              </Typography>
            </Box>
          </Box>
        )
      )}
    </Box>
  );
};

export default ReactComponentHost;
